'use client';
import React, { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import { useCurrency } from '@/context/CurrencyContext';

import { Loader2 } from 'lucide-react';

function getDealScoreColor(score: number): string {
  if (score >= 90) return 'bg-[#017743]';
  if (score >= 80) return 'bg-[#2e7d32]';
  return 'bg-[#388e3c]';
}

export default function FeaturedPlots() {
  const [featuredProperties, setFeaturedProperties] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const itemsPerPage = 4;
  const { formatPrice } = useCurrency();

  React.useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const res = await fetch('/api/properties?is_featured=true&limit=8');
        if (res.ok) {
          const result = await res.json();
          setFeaturedProperties(result.data || []);
        }
      } catch (err) {
        console.error('Failed to fetch featured plots:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchFeatured();
  }, []);

  const totalPages = Math.max(1, Math.ceil(featuredProperties.length / itemsPerPage));

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : totalPages - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < totalPages - 1 ? prev + 1 : 0));
  };

  const visibleProperties = featuredProperties.slice(
    currentIndex * itemsPerPage,
    currentIndex * itemsPerPage + itemsPerPage
  );

  if (loading) {
    return (
      <section className="bg-[#f8faf9] py-12">
        <div className="max-w-[1280px] mx-auto px-4 lg:px-6 flex flex-col items-center justify-center min-h-[300px]">
          <Loader2 size={40} className="animate-spin text-[#006838]" />
          <p className="mt-4 text-xs font-black uppercase tracking-widest text-slate-400">
            Scanning Library...
          </p>
        </div>
      </section>
    );
  }

  if (featuredProperties.length === 0) return null;

  return (
    <section className="bg-[#f8faf9] py-12">
      <div className="max-w-[1280px] mx-auto px-4 lg:px-6">
        {/* Section Header */}
        <div className="text-center mb-8">
          <h2 className="text-[32px] font-semibold text-[#1a1a1a] mb-2">
            Explore Our Prime Featured Plots
          </h2>
          <p className="text-[15px] text-[#50514f]">
            Handpicked for Value, Location &amp; Potential.
          </p>
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-end gap-2 mb-4">
          <button
            onClick={handlePrev}
            className="px-4 py-2 border border-gray-300 text-[13px] text-[#50514f] hover:bg-gray-50 transition-colors rounded"
          >
            Prev
          </button>
          <button
            onClick={handleNext}
            className="px-4 py-2 border border-gray-300 text-[13px] text-[#50514f] hover:bg-gray-50 transition-colors rounded"
          >
            Next
          </button>
        </div>

        {/* Property Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {visibleProperties.map((property) => {
            const isSold =
              (property.status as string)?.split(',').some((s: string) => s.trim().toLowerCase() === 'sold') || false;
            
            // Calculate Deal Score
            let dealScore = property.pl_score_total;
            if (dealScore === undefined || dealScore === null || dealScore === 0) {
              const lowerTitle = (property.title || '').toLowerCase();
              if (lowerTitle.includes('meydan')) {
                dealScore = 60;
              } else if (lowerTitle.includes('hotel plot')) {
                dealScore = 90;
              } else if (lowerTitle.includes('villa plots')) {
                dealScore = 85;
              } else if (lowerTitle.includes('jebel ali hills')) {
                dealScore = 90;
              } else if (lowerTitle.includes('developer plot')) {
                dealScore = 80 + (property.id % 11);
              } else {
                dealScore = 80 + (property.id % 16);
              }
            }
            dealScore = Math.max(60, Math.min(100, Number(dealScore)));
            
            const plotSize = property.land_area || property.area_size || 'N/A';
            const builtUpArea = property.area_size || 'N/A';
            const imageSrc = property.featured_image || property.image || 'https://placehold.co/800x600?text=Property';
            const propertyUrl = `/property/${property.slug || property.id}`;

            return (
              <div
                key={property.id}
                className="bg-white rounded-lg overflow-hidden shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200 group"
              >
                {/* Image */}
                <div className="relative">
                  <a href={propertyUrl}>
                    <div className="relative h-[220px] overflow-hidden">
                      <AppImage
                        src={imageSrc}
                        alt={property.title || 'Property'}
                        fill
                        className="object-cover hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  </a>

                  {/* SVG Distressed Stamp Filter definition (hidden but scoped) */}
                  <svg className="absolute w-0 h-0" width="0" height="0">
                    <defs>
                      <filter id="stamp-grunge">
                        <feTurbulence
                          type="fractalNoise"
                          baseFrequency="0.7"
                          numOctaves="5"
                          result="noise"
                        />
                        <feDisplacementMap
                          in="SourceGraphic"
                          in2="noise"
                          scale="5.5"
                          xChannelSelector="R"
                          yChannelSelector="G"
                        />
                      </filter>
                    </defs>
                  </svg>

                  {/* Distressed "Sold" Seal stamp overlay */}
                  {isSold && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/15 z-10 backdrop-blur-[0.5px] pointer-events-none select-none">
                      <div
                        className="relative flex items-center justify-center w-48 h-24 border-[6px] border-double border-white flex-shrink-0 rotate-[-12deg] bg-black/40 backdrop-blur-[0.5px] shadow-[0_0_30px_rgba(255,255,255,0.6),inset_0_0_30px_rgba(255,255,255,0.4)]"
                        style={{ filter: 'url(#stamp-grunge)' }}
                      >
                        <div className="w-full h-full border-[3px] border-white flex items-center justify-center p-4">
                          <span className="text-5xl font-black tracking-widest text-white leading-none animate-pulse">
                            SOLD
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Badges - Stacked Featured & Verified */}
                  <div className="absolute top-2 left-2 flex flex-col gap-1.5 z-10">
                    <span className="bg-[#82BC41] text-white text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-[2px] shadow-md transition-transform duration-300 hover:scale-105">
                      FEATURED
                    </span>
                    <span className="bg-[#B30000] text-white text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-[2px] shadow-md transition-transform duration-300 hover:scale-105">
                      VERIFIED
                    </span>
                  </div>

                  {/* Dynamic Plot Score Circle Progress Gauge & Badge */}
                  <div className="absolute top-2 right-2 flex flex-col items-center z-10">
                    {/* Round gauge - Always visible with micro-interaction on card hover */}
                    <div className="relative w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-lg border border-slate-100/50 transition-transform duration-300 group-hover:scale-105">
                      <svg className="w-full h-full transform -rotate-90">
                        <circle
                          cx="24"
                          cy="24"
                          r="18"
                          className="stroke-slate-100"
                          strokeWidth="4"
                          fill="transparent"
                        />
                        <circle
                          cx="24"
                          cy="24"
                          r="18"
                          className="stroke-[#006838]"
                          strokeWidth="4"
                          fill="transparent"
                          strokeDasharray={113.1}
                          strokeDashoffset={113.1 - (dealScore / 100) * 113.1}
                          strokeLinecap="round"
                        />
                      </svg>
                      <span className="absolute text-[13px] font-black text-[#006838] tracking-tight leading-none">
                        {dealScore}
                      </span>
                    </div>

                    {/* Text Badge - Only visible on hover with a premium fade and slide transition */}
                    <div className="bg-[#006838] text-white text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-[2px] shadow-md mt-1 whitespace-nowrap opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-300 pointer-events-none group-hover:pointer-events-auto">
                      Deal Score
                    </div>
                  </div>

                  {/* Price */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-3 py-2">
                    <span className="text-white text-[13px] font-semibold">
                      {formatPrice(property.price)}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-3">
                  <h3 className="text-[13px] font-semibold text-[#1a1a1a] mb-2 line-clamp-2 leading-snug">
                    <a href={propertyUrl} className="hover:text-[#017743] transition-colors">
                      {property.title}
                    </a>
                  </h3>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-[12px] text-[#50514f]">
                    {plotSize && plotSize !== 'N/A' && (
                      <span className="flex items-center gap-1">
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                          />
                        </svg>
                        {plotSize} {property.area_unit || ''}
                      </span>
                    )}
                    {builtUpArea && builtUpArea !== 'N/A' && (
                      <span className="flex items-center gap-1">
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                          />
                        </svg>
                        {builtUpArea} {property.area_unit || ''}
                      </span>
                    )}
                    {property.type && (
                      <span className="text-[#017743] font-medium">{property.type}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
